import { useTheme } from "@mui/material";
import { FolderPlus } from "lucide-react";
import type { FC } from "react";
import { useDocumentEditMutation } from "web_myplann/Api/Document/useDocumentEditMutation";
import { useDocumentListQuery } from "web_myplann/Api/Document/useDocumentListQuery";
import type { DocumentDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { usePinDrawerStep } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/usePinDrawerStep";
import { NewFolderForm } from "web_myplann/Portfolio/Forms/NewFolderForm/NewFolderForm";
import { FlatRowSelectOptionList } from "web_myplann/Shared/Components/FlatRow/FlatRowSelectOptionList";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

const R = Resources.Portfolio.List;

type Step = "list" | "new";

type Props = {
	open: boolean;
	onClose: () => void;
	item: DocumentDTO;
	activeTag?: string;
};

export const MoveToFolderDrawer: FC<Props> = ({
	open,
	onClose,
	item,
	activeTag,
}) => {
	const { palette, iconSize } = useTheme();
	const { t } = useResource();
	const { data } = useDocumentListQuery();
	const { mutate: editDocument } = useDocumentEditMutation();
	const { step, setStep } = usePinDrawerStep<Step>(open, "list");

	const folders = (data?.tags ?? []).map((tag) => tag.title);

	const moveToFolder = (folder: string) => {
		editDocument({
			requestContract: { tags: [folder] },
			id: item.id,
			activeTag,
		});
		onClose();
	};

	return (
		<>
			<MyPlannDrawer
				open={open && step === "list"}
				grabber
				onClose={onClose}
				title={t(R.DocumentActionsDrawer.fileDrawerTitle)}
			>
				<FlatRowSelectOptionList
					options={folders.map((folder) => ({ value: folder, label: folder }))}
					value={activeTag ?? ""}
					onSelect={moveToFolder}
					bottomAction={{
						label: t(R.MoveDocumentDialog.newFolderTitle),
						icon: <FolderPlus size={iconSize.md} color={palette.orange[500]} />,
						color: palette.orange[600],
						onClick: () => setStep("new"),
					}}
				/>
			</MyPlannDrawer>

			<MyPlannDrawer
				open={open && step === "new"}
				onClose={() => setStep("list")}
				title={t(R.MoveDocumentDialog.newFolderTitle)}
				grabber
			>
				<NewFolderForm
					label={t(R.DocumentActionsDrawer.Rename.folderRename)}
					placeholder={t(R.MoveDocumentDialog.newFolderPlaceholder)}
					submitLabel={t(R.MoveDocumentDialog.createAndMove)}
					onSubmit={moveToFolder}
				/>
			</MyPlannDrawer>
		</>
	);
};
